/**
 * Customer Portal Amount Utilities
 * 
 * Banking-grade precision utilities for monetary calculations and display.
 * Follows industry standards (Stripe, Square, QuickBooks) for amount handling.
 * 
 * Key Principles:
 * - All calculations performed in cents for precision
 * - Uses Intl.NumberFormat for display formatting
 * - Graceful handling of null/undefined values
 * - No floating-point arithmetic for monetary calculations
 */

/**
 * Format cents as currency display
 * Converts cents (BIGINT) to properly formatted currency string
 * 
 * @param {number} amountCents - Amount in cents (e.g., 10000 for $100.00)
 * @returns {string} Formatted currency string (e.g., "$100.00")
 */
export const formatCentsAsCurrency = (amountCents) => {
  const dollars = (amountCents || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(dollars);
};

/**
 * Calculate amount due with proper precision
 * Performs calculation in cents to avoid floating-point errors
 * 
 * @param {number} totalCents - Total amount in cents
 * @param {number} paidCents - Amount paid in cents
 * @returns {number} Amount due in cents
 */
export const calculateAmountDue = (totalCents, paidCents) => {
  const totalAmount = totalCents || 0;
  const paidAmount = paidCents || 0;
  return totalAmount - paidAmount;
};

/**
 * Convert dollars to cents for payment processing
 * Used when accepting dollar input from payment forms
 * 
 * @param {string|number} dollars - Dollar amount (e.g., "100.00" or 100)
 * @returns {number} Amount in cents (e.g., 10000)
 */
export const dollarsToCents = (dollars) => {
  return Math.round((parseFloat(dollars) || 0) * 100);
};

/**
 * Format dollar amounts as currency
 * Backend sends dollar amounts (already converted from cents)
 * 
 * @param {number} amount - Amount in dollars (e.g., 60.15 for $60.15)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '$0.00';
  
  // Backend provides dollar amounts, format directly
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Format cents as dollars for display (no currency symbol)
 * Useful for calculations or when currency symbol is shown separately
 * 
 * @param {number} amountCents - Amount in cents
 * @returns {string} Dollar amount as string (e.g., "100.00")
 */
export const centsToDollars = (amountCents) => {
  return ((amountCents || 0) / 100).toFixed(2);
};

/**
 * Sum multiple amounts in cents
 * Safely adds multiple cent values with null/undefined handling
 * 
 * @param {number[]} amounts - Array of amounts in cents
 * @returns {number} Total amount in cents
 */
export const sumAmountsInCents = (amounts) => {
  return amounts.reduce((total, amount) => total + (amount || 0), 0);
};

/**
 * Validate amount input
 * Ensures amount is a valid monetary value
 * 
 * @param {any} amount - Amount to validate
 * @returns {boolean} True if valid monetary amount
 */
export const isValidAmount = (amount) => {
  const num = parseFloat(amount);
  return !isNaN(num) && num >= 0 && isFinite(num);
};